import React, { useEffect, useRef, useState } from 'react';
import { useTimeRange, TimeRangeType } from '../../context/TimeRangeContext';
import { format } from 'date-fns';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { ReactComponent as CalendarIcon } from '../../images/datepicker-calendar.svg';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { getStartEndDateFromRange } from '../../utilities/DateUtils';
import {
  fetchAssetTrendInitialStateAsync,
  setDateRangeAndAggregate,
  setIsCustomRange,
  resetAssetTrendDataState,
  setEnableLiveToggle,
  setLiveDataEnabled,
  setLiveDataTimeRange,
} from '../slices/AssetTrendDataSlice';
import { fetchInitialWellChartDataAsync, setInitialState } from '../slices/WellTestChartDataSlice';
import { DashboardConfig, saveUserPerferences } from '../../dispatchers/UserPreferenceDispatcher';
import { fetchDailyAverageInitialStateAsync } from '../slices/DailyAverageDataSlice';
import dayjs from 'dayjs';
import {
  fetchRodLiftAnalysisCardDatesStreamAsync,
  resetCardDateListState,
} from '../../features/rod-lift-analysis/CardDateSlice';
import { fetchAssetTrendByTagsStateAsync } from '../slices/AssetTrendDataByTagsSlice';
import {
  ESP_ANALYSIS_BY_TAGS_PARAM_STD_TYPES,
  ESP_ASSET_TREND_BY_TAGS_PARAM_STD_TYPES,
  GL_ANALYSIS_BY_TAGS_PARAM_STD_TYPES,
  GL_ASSET_TREND_BY_TAGS_PARAM_STD_TYPES,
} from '../../constants/asset-trend-by-tags.constant';
import { fetchAnalysisChartData } from '../../services/dashboard/AnalysisChartService';
import { hideTooltip, showTooltip } from '../common/simple-tool-tip/TooltipManager';
import RefreshIcon from '../../features/asset-status/icons/RefreshIcon';
import ToggleOption from '../common/ToggleOption';
import './TimeRangeSelector.scss';
import useConfig from '../../hooks/useConfig';
import { clearSelectedRanges } from '../../features/contextualizer/selectedRangesSlice';
import { useLocation } from 'react-router-dom';
import { isLiveStreamingEnabled } from '../../services/configuration/config';

interface TimeRangeButtonProps {
  label: TimeRangeType;
  isActive?: boolean;
  onClick: () => void;
}

const TimeRangeButton: React.FC<TimeRangeButtonProps> = ({ label, isActive = false, onClick }) => {
  const { enablePumpChecker } = useConfig();

  const isDisabled =
    !enablePumpChecker && (label?.toLowerCase() === 'pop' || label?.toLowerCase() === 'current compl.');

  return (
    <button
      analytics-id={`time-range-btn-${(label ?? '').replace(/\s+/g, '-').toLowerCase()}`}
      className={`time-range-button ${isActive ? 'time-range-button--active' : ''} ${isDisabled ? 'time-range-button--disabled' : ''}`}
      onClick={onClick}
      disabled={isDisabled}
    >
      <span
        className={`time-range-button__text ${
          isActive ? 'time-range-button__text--active' : 'time-range-button__text--inactive'
        }`}
      >
        {label}
      </span>
    </button>
  );
};
interface TimeSeriesDataProps {
  timeseriesChartData: any;
  retrieveMinMax: boolean;
  assetId: any;
  fetchCov: boolean;
  aggregateMethod: string;
  layout: string;
  enableRefreshOnTimeRangeSelector?: boolean;
}
const TimeRangeSelector: React.FC<TimeSeriesDataProps> = ({
  timeseriesChartData,
  retrieveMinMax,
  assetId,
  fetchCov,
  aggregateMethod,
  layout,
  enableRefreshOnTimeRangeSelector,
}) => {
  const { selectedRange, setSelectedRange } = useTimeRange();
  const chartType = useAppSelector((state) => state.AssetAnalysis.chartType);
  const chartData = useAppSelector((state: any) => state.AssetTrendDataSlice.data);
  const datePickerRef = useRef<HTMLDivElement | null>(null); // Ref for the calendar component
  const dispatch = useAppDispatch();
  const isTestDashboard = chartType?.toLowerCase().includes('tests');

  const location = useLocation();
  const chartsTab = location.pathname.includes('charts');

  // Array of all time range options
  const baseTimeRanges: TimeRangeType[] = ['24H', '7D', '14D', '30D', '90D', '6M', '1Y'];
  const extendedTimeRanges: TimeRangeType[] = ['POP', 'Current compl.'];
  const timeRanges: TimeRangeType[] = isTestDashboard
    ? extendedTimeRanges
    : chartType === 'rl-well-charts'
      ? baseTimeRanges
      : [...baseTimeRanges, ...extendedTimeRanges];

  const [dateRange, setDateRange] = useState<{ startDate: Date; endDate: Date } | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [liveStreamingEnabled, setLiveStreamingEnabled] = useState(false);
  const previousSelectedRangeRef = useRef<string | null>(null);
  const previousDateRangeRef = useRef<{ startDate: Date; endDate: Date } | null>(null);
  const isInitialMountRef = useRef<boolean>(true);
  const previousTimeSeriesChartRef = useRef<any>(null);
  const previousDateSelectorRef = useRef<any>(null);

  const wellTestId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const wellTestMOP = useAppSelector((state: any) => state.WellTestIdSlice?.data?.MethodOfProduction);
  const isLiveDataEnabled = useAppSelector((state: any) => state.AssetTrendDataSlice.isLiveDataEnabled);
  const enableLiveToggle = useAppSelector((state: any) => state.AssetTrendDataSlice.enableLiveToggle);

  // Keep track of previous assetId for cleanup
  const previousAssetIdRef = useRef(assetId);

  // Handle async isLiveStreamingEnabled check
  useEffect(() => {
    const checkLiveStreamingEnabled = async () => {
      try {
        const enabled = await isLiveStreamingEnabled();
        setLiveStreamingEnabled(enabled);
      } catch (error) {
        console.error('Error checking live streaming status:', error);
        setLiveStreamingEnabled(false);
      }
    };
    checkLiveStreamingEnabled();
  }, []);

  // Component cleanup - reset state when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetAssetTrendDataState());
    };
  }, []);

  // Asset change cleanup - reset state when assetId changes
  useEffect(() => {
    const previousAssetId = previousAssetIdRef.current;
    if (previousAssetId && previousAssetId !== assetId) {
      dispatch(resetAssetTrendDataState());
    }
    previousAssetIdRef.current = assetId;
  }, [assetId]);

  useEffect(() => {
    if (isTestDashboard && !dateRange && !selectedRange) {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 7);

      setDateRange({ startDate, endDate });
      dispatch(setIsCustomRange(true));
    }
  }, [isTestDashboard, dateRange, selectedRange]);

  useEffect(() => {
    if (wellTestId && dateRange) {
      const { startDate, endDate } = dateRange;

      dispatch(
        fetchInitialWellChartDataAsync({
          wellId: wellTestId,
          startDate: startDate,
          endDate: endDate,
        }),
      );
    } else {
      dispatch(setInitialState());
    }
  }, [wellTestId, dateRange, assetId]);
  const popDate = useAppSelector((state: any) => state.WellTestChartDataSlice?.popDate);
  const curCompDate = useAppSelector((state: any) => state.WellTestChartDataSlice?.currentInstall);
  const viewType = useAppSelector((state: any) => state.DailyAverageDataSlice?.viewType);

  const calculateAggregate = (diffInDays: number): string => {
    if (diffInDays <= 1) {
      return '1m';
    } else if (diffInDays <= 14) {
      return '10m';
    } else if (diffInDays <= 30) {
      return '30m';
    } else if (diffInDays <= 90) {
      return '2h';
    } else {
      return '1d';
    }
  };

  const handleApply = (range: { startDate: Date; endDate: Date }) => {
    setDateRange(range);
    setSelectedRange(null);
    setIsDatePickerOpen(false);
    dispatch(setIsCustomRange(true));
    dispatch(clearSelectedRanges()); // Clear selected ranges in the Contextualizer
    dispatch(setLiveDataEnabled(false));
    dispatch(setEnableLiveToggle(false));
  };

  const liveDataEnableDisable = (range: string | null) => {
    if (range == '24H') {
      dispatch(setLiveDataTimeRange(range));
      dispatch(setEnableLiveToggle(true));
    } else {
      dispatch(setLiveDataEnabled(false));
      dispatch(setLiveDataTimeRange(''));
      dispatch(setEnableLiveToggle(false));
    }
  };

  const handleTimeRangeButtonClick = (range: TimeRangeType) => {
    setSelectedRange(range);

    let startDate: Date;
    const endDate = getStartEndDateFromRange(range).endDate;

    if (range === 'POP' && popDate) {
      startDate = new Date(popDate);
    } else if (range === 'Current compl.' && curCompDate) {
      startDate = new Date(curCompDate);
    } else {
      startDate = getStartEndDateFromRange(range).startDate;
    }

    setDateRange({ startDate, endDate });
    dispatch(setIsCustomRange(false));
    liveDataEnableDisable(range);
  };

  const handleCancel = () => {
    setIsDatePickerOpen(false);
  };

  const userPreferenceData = useAppSelector((state: any) => state.testWellColumnsSlice.data?.widgets);
  const timeSeriesChart = isTestDashboard
    ? userPreferenceData?.find((item: any) => item.key === (layout === 'All' ? 'wellTestChart' : 'wellTestSplitChart'))
    : userPreferenceData?.find((item: any) => item.key === 'timeseriesChart');
  const dateSelector = timeSeriesChart?.widgetProperty?.widgetProperties?.dateSelector;

  useEffect(() => {
    const initialInterval = dateSelector?.interval;
    const customStartDate = dateSelector?.custom?.startDate;
    const customEndDate = dateSelector?.custom?.endDate;

    // Track when dateSelector changes to prevent saving when loading from preferences
    const dateSelectorChanged = previousDateSelectorRef.current !== dateSelector;
    previousDateSelectorRef.current = dateSelector;

    if (initialInterval && initialInterval !== '') {
      setSelectedRange(initialInterval);
      liveDataEnableDisable(initialInterval);

      // If loading from dateSelector, reset initial mount flag to prevent saving
      if (dateSelectorChanged) {
        isInitialMountRef.current = true;
      }
    }

    if (customStartDate && customEndDate) {
      setDateRange({
        startDate: new Date(customStartDate),
        endDate: new Date(customEndDate),
      });
      // Only clear liveDataTimeRange if this is truly a custom range (no interval)
      if (!initialInterval || initialInterval === '') {
        dispatch(setLiveDataTimeRange(''));
      }
      // If loading from dateSelector, reset initial mount flag to prevent saving
      if (dateSelectorChanged) {
        isInitialMountRef.current = true;
      }
    }
  }, [dateSelector]);

  useEffect(() => {
    if (selectedRange) {
      const predefinedRanges = ['24H', '7D', '14D', '30D', '90D', '6M', '1Y', 'POP', 'Current compl.'];
      if (predefinedRanges.includes(selectedRange)) {
        let startDate: Date;
        const endDate = getStartEndDateFromRange(selectedRange).endDate;

        if (selectedRange === 'POP' && popDate) {
          startDate = new Date(popDate);
        } else if (selectedRange === 'Current compl.' && curCompDate) {
          startDate = new Date(curCompDate);
        } else {
          startDate = getStartEndDateFromRange(selectedRange).startDate;
        }

        setDateRange({ startDate, endDate });
        dispatch(setIsCustomRange(false));

        liveDataEnableDisable(selectedRange);
        return;
      }
    }
    if (!selectedRange && dateRange) {
      dispatch(setIsCustomRange(true));
    }
  }, [selectedRange, dispatch, chartsTab, popDate, curCompDate]);

  const getSelectedColumnsAsCSV = (columns: any) => {
    const timeSeriesWidget = isTestDashboard
      ? columns?.find((item: any) => item.key === (layout === 'All' ? 'wellTestChart' : 'wellTestSplitChart'))
      : columns?.find((item: any) => item.key === 'timeseriesChart');

    if (!timeSeriesWidget) {
      return '';
    }

    const parameters = timeSeriesWidget?.widgetProperty?.widgetProperties?.parameterConfig?.[0]?.parameters || [];

    return parameters
      .filter((param: any) => param?.selected === true)
      .map((param: any) => param?.address)
      .filter(Boolean)
      .join(',');
  };

  const isRLChart = chartType === 'rl-well-charts';
  const isFacilityChart = chartType === 'facility-well-charts';
  const isAnyChartZoomed = useAppSelector((state: any) => state.AssetTrendDataSlice.isAnyChartZoomed);

  useEffect(() => {
    const fetchData = async () => {
      dispatch(clearSelectedRanges()); // Clear selected ranges in the Contextualizer

      if (chartType === 'rl-well-charts' && dateRange) {
        const { startDate, endDate } = dateRange;
        dispatch(
          fetchRodLiftAnalysisCardDatesStreamAsync({
            assetId,
            version: 'v3',
            startDate: dayjs(startDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
            endDate: dayjs(endDate).format('YYYY-MM-DD HH:mm:ss.SSS'),
          }),
        );
      } else {
        dispatch(resetCardDateListState());
      }

      if (!assetId || !(selectedRange || dateRange)) return;
      let startDate: Date;
      let endDate: Date;

      if (dateRange) {
        startDate = dateRange.startDate;
        endDate = dateRange.endDate;
      } else if (selectedRange) {
        if (selectedRange === 'POP' && popDate) {
          startDate = new Date(popDate);
          endDate = getStartEndDateFromRange(selectedRange).endDate;
        } else if (selectedRange === 'Current compl.' && curCompDate) {
          startDate = new Date(curCompDate);
          endDate = getStartEndDateFromRange(selectedRange).endDate;
        } else {
          const rangeDates = getStartEndDateFromRange(selectedRange);
          startDate = rangeDates.startDate;
          endDate = rangeDates.endDate;
        }
      } else {
        return;
      }

      const diffInMs = endDate.getTime() - startDate.getTime();
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

      const aggregate = calculateAggregate(diffInDays);

      dispatch(setDateRangeAndAggregate({ startDate, endDate, aggregate }));

      if (viewType === 'Daily average' && assetId && dateRange) {
        dispatch(
          fetchDailyAverageInitialStateAsync({
            assetId,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            rodlift: isRLChart || isFacilityChart,
          }),
        );
      } else {
        if (layout === 'Split') {
          await dispatch(
            fetchAssetTrendByTagsStateAsync({
              assetId,
              startDate,
              endDate,
              aggregate: isTestDashboard ? '1d' : aggregate,
              aggregateMethod: aggregateMethod || 'last',
              paramStdTypes:
                chartType === 'esp-well-tests'
                  ? ESP_ASSET_TREND_BY_TAGS_PARAM_STD_TYPES
                  : GL_ASSET_TREND_BY_TAGS_PARAM_STD_TYPES,
            }),
          );
        } else {
          await dispatch(
            fetchAssetTrendInitialStateAsync({
              assetId,
              startDate,
              endDate,
              aggregate: isTestDashboard ? '1d' : aggregate,
              aggregateMethod: aggregateMethod || 'last',
              timeseriesChartData,
              retrieveMinMax,
              fetchCov,
              chartType,
              isZoomedIn: isAnyChartZoomed,
              ...(chartData?.length > 0 && isRLChart ? { Addresses: getSelectedColumnsAsCSV(userPreferenceData) } : {}),
            }),
          );
          if (layout.toLowerCase() === 'all') {
            if (wellTestId && wellTestMOP) {
              await dispatch(
                fetchAnalysisChartData({
                  wellId: wellTestId,
                  MOP: wellTestMOP,
                }),
              );
            }
            await dispatch(
              fetchAssetTrendByTagsStateAsync({
                assetId,
                startDate,
                endDate,
                aggregate: isTestDashboard ? '1d' : aggregate,
                aggregateMethod: aggregateMethod || 'last',
                paramStdTypes:
                  chartType === 'esp-well-tests'
                    ? ESP_ANALYSIS_BY_TAGS_PARAM_STD_TYPES
                    : GL_ANALYSIS_BY_TAGS_PARAM_STD_TYPES,
              }),
            );
          }
        }
      }
    };
    fetchData();
  }, [selectedRange, dateRange, assetId, retrieveMinMax, fetchCov, aggregateMethod, chartType, isManualRefresh]);

  useEffect(() => {
    // Reset initial mount flag when timeSeriesChart changes (layout switch)
    if (previousTimeSeriesChartRef.current !== timeSeriesChart && timeSeriesChart) {
      isInitialMountRef.current = true;
      previousTimeSeriesChartRef.current = timeSeriesChart;
      // Reset previous values when switching layouts
      previousSelectedRangeRef.current = null;
      previousDateRangeRef.current = null;
    }

    if (!timeSeriesChart || (!selectedRange && !dateRange)) {
      // Update refs even if we return early
      previousSelectedRangeRef.current = selectedRange;
      previousDateRangeRef.current = dateRange;
      return;
    }

    // Skip saving on initial mount - only save when values actually change
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousSelectedRangeRef.current = selectedRange;
      previousDateRangeRef.current = dateRange;
      return;
    }

    // Check if selectedRange changed (POP or Current compl.)
    const selectedRangeChanged = previousSelectedRangeRef.current !== selectedRange;

    // Check if dateRange changed (custom date selection)
    const dateRangeChanged =
      previousDateRangeRef.current === null ||
      dateRange === null ||
      previousDateRangeRef.current.startDate.getTime() !== dateRange.startDate.getTime() ||
      previousDateRangeRef.current.endDate.getTime() !== dateRange.endDate.getTime();

    // Only save if there's an actual user change
    if (selectedRangeChanged || dateRangeChanged) {
      const transformedTimeWindowSelection: DashboardConfig = {
        DashboardName: chartType,
        WidgetName: timeSeriesChart?.widgetProperty?.key ?? '',
        PropertyType: 'DateSelector',
        Preferences: {
          Interval: selectedRange || '',
          Custom: {
            StartDate: dayjs(dateRange?.startDate).format('YYYY-MM-DD HH:mm:ss.SSS') || '',
            EndDate: dayjs(dateRange?.endDate).format('YYYY-MM-DD HH:mm:ss.SSS') || '',
          },
          CurrentCompletion: selectedRange === 'Current compl.' ? 'true' : 'false',
          Pop: selectedRange === 'POP' ? 'true' : 'false',
        },
        ...((chartType === 'rl-well-charts' || chartType === 'facility-well-charts') && assetId
          ? { assetGUID: assetId }
          : {}),
      };

      dispatch(saveUserPerferences(transformedTimeWindowSelection));
    }

    // Update refs to track current values
    previousSelectedRangeRef.current = selectedRange;
    previousDateRangeRef.current = dateRange;
  }, [selectedRange, dateRange, timeSeriesChart, chartType, dispatch]);

  // Close the calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsDatePickerOpen(false); // Close the calendar
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const pageDataRefresh = () => {
    setIsManualRefresh(!isManualRefresh);
  };

  const handleLiveDataToggle = (enabled: boolean) => {
    dispatch(setLiveDataEnabled(enabled));
  };

  return (
    <div className='flex flex-row'>
      <div
        className='time-range-selector'
        style={{
          width: `${isTestDashboard ? '212px' : chartType === 'rl-well-charts' ? '452px' : '660px'}`,
        }}
      >
        <div className='time-range-selector__buttons'>
          {timeRanges.map((range) => (
            <TimeRangeButton
              key={range}
              label={range}
              isActive={selectedRange === range}
              onClick={() => handleTimeRangeButtonClick(range)}
            />
          ))}
        </div>
      </div>
      <div className='time-range-selector__dropdown' onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}>
        <span className='date-range-display'>
          <CalendarIcon />
          {dateRange
            ? `${format(dateRange.startDate, 'MMM d, yyyy')} - ${format(dateRange.endDate, 'MMM d, yyyy')}`
            : selectedRange
              ? (() => {
                  const { startDate, endDate } = getStartEndDateFromRange(selectedRange);
                  return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
                })()
              : 'Select Date Range'}
        </span>
      </div>

      {isDatePickerOpen && (
        <div ref={datePickerRef} className='absolute z-[9999] mt-2'>
          <DateRangePicker
            onApply={handleApply}
            onCancel={handleCancel}
            initialStartDate={
              dateRange
                ? dateRange.startDate
                : selectedRange
                  ? getStartEndDateFromRange(selectedRange).startDate
                  : undefined
            }
            initialEndDate={
              dateRange
                ? dateRange.endDate
                : selectedRange
                  ? getStartEndDateFromRange(selectedRange).endDate
                  : undefined
            }
          />
        </div>
      )}
      {enableRefreshOnTimeRangeSelector && (
        <div className='refresh-button-container' style={{ display: 'flex', alignItems: 'center' }}>
          {chartType === 'rl-well-charts' && liveStreamingEnabled && (
            <div
              onMouseEnter={(e) => {
                if (!enableLiveToggle) {
                  showTooltip(
                    'Live data is only available for 24H time ranges of Stacked, 2-Y, and Multi-Y views',
                    e.currentTarget,
                  );
                }
              }}
              onMouseLeave={hideTooltip}
              style={{
                opacity: enableLiveToggle ? 1 : 0.5,
                cursor: enableLiveToggle ? 'pointer' : 'not-allowed',
              }}
            >
              <ToggleOption
                label='Live data'
                enabled={isLiveDataEnabled}
                onChange={handleLiveDataToggle}
                className='live-data-toggle'
                disabled={!enableLiveToggle}
              />
            </div>
          )}
          <button
            className='refresh-button'
            onClick={pageDataRefresh}
            onMouseEnter={(e) => showTooltip('Refresh data', e.currentTarget)}
            onMouseLeave={hideTooltip}
          >
            <RefreshIcon />
          </button>
        </div>
      )}
    </div>
  );
};

export default TimeRangeSelector;
